import { match } from 'ts-pattern';

import {
  SurveyChannelCreationConfirmedEvent,
  SurveyQuota,
} from '@ps/survey-utils';
import { BasePayloadMapper } from '@ps/utils';

import { LaunchTrafficChannelRequestDto } from '../dtos/survey.dto';
import { QuotaCriterionDto } from '../dtos/survey-quota.dto';
import { Option } from '@sapphire/result';
import { QuotaCriteriaPayloadMapper } from './survey-quota-payload.mapper';

type TcQuotaIdPayload = { id: 'all' | string };
type TcQuotaCriteriaPayload = TcQuotaIdPayload & {
  category: string;
  name: QuotaCriterionDto[];
};
type TcQuotaProgramField = Partial<
  Pick<LaunchTrafficChannelRequestDto, 'aidaQuota' | 'qbpQuota'>
>;

export class LaunchTrafficChannelPayloadMapper extends BasePayloadMapper<
  SurveyChannelCreationConfirmedEvent,
  LaunchTrafficChannelRequestDto
> {
  public toPayload(
    event: SurveyChannelCreationConfirmedEvent,
  ): LaunchTrafficChannelRequestDto {
    const quotaProgramField = this._buildQuotaProgramField(event);

    return match(event)
      .with({ cardType: 'replica' }, (replicaEvent) => ({
        channelSurveyName: replicaEvent.surveyTitle,
        channelType: 'REP',
        payload: {},
        copyTargetingFromParent: true,
        ...quotaProgramField,
      }))
      .with({ cardType: 'targeted-list' }, (targetedListEvent) => ({
        channelSurveyName: targetedListEvent.surveyTitle,
        channelType: 'TL',
        payload: { filePath: targetedListEvent.filePath },
        copyTargetingFromParent: false,
        ...quotaProgramField,
      }))
      .exhaustive();
  }

  private _buildQuotaProgramField(
    event: SurveyChannelCreationConfirmedEvent,
  ): TcQuotaProgramField {
    return Option.all([event.quotaProgram, event.selectedQuota]).mapOr(
      {},
      ([program, quota]) => ({
        [`${program}Quota`]: this._mapQuotaPayload(quota),
      }),
    );
  }

  private _mapQuotaPayload(
    quota: SurveyQuota | 'all',
  ): TcQuotaCriteriaPayload | TcQuotaIdPayload {
    if (quota === 'all') {
      return { id: 'all' };
    }

    const _criterionPayloadMapper = new QuotaCriteriaPayloadMapper();
    return {
      id: quota.id,
      category: quota.label,
      name: _criterionPayloadMapper.toPayload(quota.criteria),
    };
  }
}
